import React from "react";
import { Printer } from "lucide-react";

// Clean, structured, single-page company overview that doubles as Bud Link's
// sales-deck PDF for Dutchie / Jane / Leafly / Weedmaps Partner API
// applications. Designed for LLM-assisted evaluator parsing: short
// declarative sentences, hard numbers, named entities, clear section
// headers — no marketing prose. Render in browser → Save as PDF → upload
// to the partner application portal.

const STATS = [
  { v: "31", l: "PA dispensaries syncing live menus today" },
  { v: "2", l: "active Partner API integrations (Cresco, Trulieve)" },
  { v: "16", l: "additional PA dispensaries pending Dutchie approval" },
  { v: "~430K", l: "active PA medical-cannabis patient cards (2026)" },
];

const TRACTION = [
  {
    k: "Live Partner API integrations",
    v: "Cresco Labs (16 Sunnyside locations) · Trulieve Pennsylvania (15 locations)",
  },
  {
    k: "Dispensaries in directory",
    v: "47+ licensed Pennsylvania medical-cannabis retailers across all 6 PA medical regions",
  },
  {
    k: "Sync frequency",
    v: "Hourly background refresh via APScheduler; near-real-time on Partner API webhook (where available)",
  },
  {
    k: "Product index size",
    v: "11,300+ active SKUs across flower, vape, concentrate, edible, tincture, and topical categories",
  },
  {
    k: "Production URL",
    v: "https://budlink.linklitepro.com",
  },
];

const INTEGRATION = [
  {
    k: "Integration type",
    v: "eCommerce — read-only menu consumer. We do not handle cart, payment, fulfillment, or KYC.",
  },
  {
    k: "Data consumed",
    v: "Product catalog · live inventory · pricing · cannabinoid percentages · stock status · product imagery · strain metadata",
  },
  {
    k: "Order path",
    v: "Every product card deep-links to the dispensary's existing Dutchie checkout page. Bud Link does not intercept transactions.",
  },
  {
    k: "Data residency",
    v: "MongoDB Atlas (US East). All menu data refreshed against source-of-truth on every sync; no long-term derivative storage of pricing or inventory.",
  },
  {
    k: "Compliance",
    v: "21+ age gate on every session · server-side PA state allow-list · no consumer transactions · medical-marijuana program adherence",
  },
];

const ASK = [
  "Dutchie Partner API access (eCommerce, read-only) for 16 currently-pending PA dispensaries operating on Dutchie POS.",
  "Estimated live-menu coverage expansion: 31 → 47+ Pennsylvania dispensaries (≈50% growth) within first week of approval.",
  "Zero disruption to existing dispensary workflow — Bud Link consumes the same data each store already publishes via Dutchie.",
];

export default function PitchDeck() {
  const print = () => window.print();
  return (
    <main className="pitch-deck">
      <header className="pd-header">
        <div>
          <div className="pd-eyebrow">Company overview · Partner API application</div>
          <h1 className="pd-h1">Bud Link</h1>
          <p className="pd-sub">
            Pennsylvania medical-cannabis product discovery and dispensary search.
          </p>
        </div>
        <button
          onClick={print}
          data-testid="pitch-deck-print"
          className="pd-print"
        >
          <Printer className="w-3.5 h-3.5" />
          Save as PDF
        </button>
      </header>

      <section className="pd-section">
        <div className="pd-section-label">01 · Company</div>
        <table className="pd-table">
          <tbody>
            <tr>
              <td>Brand</td>
              <td>Bud Link</td>
            </tr>
            <tr>
              <td>Parent</td>
              <td>LinkLite Pro — established 2008</td>
            </tr>
            <tr>
              <td>Bud Link launched</td>
              <td>2026</td>
            </tr>
            <tr>
              <td>Headquarters</td>
              <td>Pennsylvania, USA</td>
            </tr>
            <tr>
              <td>Contact</td>
              <td>William Stoudt · wstoudt@linklitepro.com</td>
            </tr>
            <tr>
              <td>Production</td>
              <td>https://budlink.linklitepro.com</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section className="pd-section">
        <div className="pd-section-label">02 · What Bud Link is</div>
        <p className="pd-body">
          Bud Link is a Pennsylvania-focused medical-cannabis search engine. PA
          card-holding patients use Bud Link to find specific products
          (strains, vapes, edibles, concentrates) available right now at
          licensed dispensaries near them — then deep-link to the dispensary&apos;s
          existing online ordering page to complete the purchase. Bud Link
          does not sell, ship, or transact cannabis; it is a discovery and
          referral layer on top of state-licensed dispensary commerce.
        </p>
      </section>

      <section className="pd-section">
        <div className="pd-section-label">03 · Market</div>
        <div className="pd-stats">
          {STATS.map((s) => (
            <div key={s.l} className="pd-stat">
              <div className="pd-stat-v">{s.v}</div>
              <div className="pd-stat-l">{s.l}</div>
            </div>
          ))}
        </div>
        <p className="pd-body">
          Pennsylvania operates one of the largest state-regulated medical-only
          cannabis markets in the United States, with roughly 430,000 active
          patient cards and a fragmented network of 180+ licensed dispensary
          locations across 6 medical regions. Existing national platforms
          (Weedmaps, Leafly) under-serve the PA medical patient: they surface
          recreational-market language, recreational pricing units, and out-
          of-state inventory. Bud Link is built PA-medical-first — every UX,
          search ranking, and filter is tuned for card-holding patients
          locating specific products at compliant in-state retailers.
        </p>
      </section>

      <section className="pd-section">
        <div className="pd-section-label">04 · Current traction</div>
        <table className="pd-table">
          <tbody>
            {TRACTION.map((r) => (
              <tr key={r.k}>
                <td>{r.k}</td>
                <td>{r.v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="pd-section">
        <div className="pd-section-label">05 · Integration model</div>
        <table className="pd-table">
          <tbody>
            {INTEGRATION.map((r) => (
              <tr key={r.k}>
                <td>{r.k}</td>
                <td>{r.v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="pd-section">
        <div className="pd-section-label">06 · Partner value (what Dutchie unlocks)</div>
        <ul className="pd-bullets">
          {ASK.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>
        <p className="pd-body">
          Bud Link is a pure traffic-driver for participating dispensaries.
          Every &ldquo;Order Now&rdquo; interaction routes a qualified, ready-to-purchase
          PA medical patient into the dispensary&apos;s existing Dutchie checkout
          flow. Bud Link captures no margin, takes no commission, and does
          not white-label the dispensary&apos;s brand. Listing in Bud Link is
          permanently free to retailers; the platform monetizes only through
          optional featured-placement add-ons clearly labeled as sponsored in
          search results.
        </p>
      </section>

      <section className="pd-section">
        <div className="pd-section-label">07 · Compliance and data integrity</div>
        <ul className="pd-bullets">
          <li>
            21+ age verification gate enforced on every browser session via
            persistent localStorage token plus server-side state allow-list.
          </li>
          <li>
            Pennsylvania state allow-list applied server-side; queries
            originating outside permitted geographies receive an explanatory
            block instead of search results.
          </li>
          <li>
            Inventory and pricing data refreshed against source-of-truth Partner
            APIs (Cresco, Trulieve today; Dutchie pending) on an hourly
            schedule; webhook-driven near-real-time updates supported where
            the partner exposes them.
          </li>
          <li>
            No consumer cannabis transactions handled by Bud Link. No payment
            processing, no fulfillment, no KYC, no compliance surface area
            beyond search and referral.
          </li>
          <li>
            Brand and product imagery cleanly fall back to a Bud Link
            placeholder when an upstream image is absent or fails to load;
            misattribution is engineered out at the data layer.
          </li>
        </ul>
      </section>

      <section className="pd-section">
        <div className="pd-section-label">08 · Ask</div>
        <p className="pd-body">
          Approve Bud Link for Dutchie Partner API (eCommerce read-only)
          access. We have already coordinated with Dutchie Partner Support
          (ticket #667778) and are running parallel outreach to each of the
          16 PA dispensary admins on Dutchie POS so their individual
          authorizations land in the same approval window.
        </p>
      </section>

      <footer className="pd-footer">
        Bud Link · LinkLite Pro · West Reading, Pennsylvania · wstoudt@linklitepro.com ·
        https://budlink.linklitepro.com · Page generated for Partner API application use.
      </footer>

      <style>{`
        .pitch-deck {
          max-width: 8.5in;
          margin: 0 auto;
          padding: 1.25rem 1.5rem 3rem;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          color: #0a0a0a;
          background: #fff;
          line-height: 1.5;
        }
        .pd-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 1rem;
          padding-bottom: 1.25rem;
          border-bottom: 2px solid #0a0a0a;
          margin-bottom: 1.5rem;
        }
        .pd-eyebrow {
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.18em;
          color: #555;
          margin-bottom: 0.4rem;
        }
        .pd-h1 {
          font-family: "Cormorant Garamond", "Times New Roman", serif;
          font-size: 3rem;
          font-weight: 600;
          line-height: 1;
          margin: 0;
        }
        .pd-sub {
          margin: 0.55rem 0 0;
          font-size: 1rem;
          color: #333;
        }
        .pd-print {
          appearance: none;
          border: 1px solid #d4d4d4;
          background: #fff;
          color: #0a0a0a;
          font-size: 11px;
          padding: 0.45rem 0.85rem;
          border-radius: 999px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
        }
        .pd-section {
          margin-bottom: 1.5rem;
          page-break-inside: avoid;
        }
        .pd-section-label {
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.18em;
          color: #6b7280;
          margin-bottom: 0.55rem;
          border-bottom: 1px solid #e5e5e5;
          padding-bottom: 0.35rem;
        }
        .pd-body {
          font-size: 0.92rem;
          color: #1f2937;
          margin: 0.4rem 0 0;
        }
        .pd-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 0.6rem;
          margin: 0.5rem 0 0.85rem;
        }
        .pd-stat {
          border: 1px solid #e5e5e5;
          border-radius: 8px;
          padding: 0.6rem 0.7rem;
        }
        .pd-stat-v {
          font-family: "Cormorant Garamond", "Times New Roman", serif;
          font-size: 1.45rem;
          font-weight: 600;
        }
        .pd-stat-l {
          font-size: 9.5px;
          color: #4b5563;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          margin-top: 0.15rem;
          line-height: 1.3;
        }
        .pd-table {
          width: 100%;
          border-collapse: collapse;
          margin: 0.35rem 0 0;
          font-size: 0.9rem;
        }
        .pd-table td {
          padding: 0.45rem 0.5rem;
          border-bottom: 1px solid #ececec;
          vertical-align: top;
        }
        .pd-table td:first-child {
          width: 30%;
          color: #4b5563;
          font-weight: 500;
        }
        .pd-bullets {
          margin: 0.4rem 0 0;
          padding-left: 1.1rem;
          font-size: 0.9rem;
          color: #1f2937;
        }
        .pd-bullets li {
          margin-bottom: 0.3rem;
        }
        .pd-footer {
          margin-top: 2rem;
          padding-top: 0.85rem;
          border-top: 1px solid #d4d4d4;
          font-size: 9.5px;
          color: #6b7280;
          text-align: center;
          letter-spacing: 0.02em;
        }
        @media (max-width: 640px) {
          .pd-stats { grid-template-columns: repeat(2, 1fr); }
          .pd-h1 { font-size: 2.4rem; }
          .pd-header { flex-direction: column; }
        }
        @media print {
          @page { size: letter; margin: 0.5in; }
          .pd-print { display: none; }
          .pitch-deck { padding: 0; }
        }
      `}</style>
    </main>
  );
}
